import * as React from 'react';
import { cn } from './lib/utils';

/**
 * Barbell chart — compares previous vs current values per row,
 * with a target indicator line.
 *
 * rows: Array<{ label, prev, curr, target, higherIsBetter, color? }>
 * unit: string (e.g. '%', '/5')
 */
export function BarbellChart({ rows = [], unit = '%', className }) {
  if (!rows.length) return null;

  const allVals = rows.flatMap(r => [r.prev, r.curr]);
  const mn = Math.min(...allVals) * 0.92;
  const mx = Math.max(...allVals) * 1.08;
  const range = mx - mn || 1;
  const pct = (v) => ((v - mn) / range) * 100;

  return (
    <div className={cn('flex flex-col gap-2 text-body', className)}>
      {rows.map((row, i) => {
        const prevPct = pct(row.prev);
        const currPct = pct(row.curr);
        const targetPct = pct(row.target);
        const improving = row.higherIsBetter ? row.curr > row.prev : row.curr < row.prev;
        const atTarget = row.higherIsBetter ? row.curr >= row.target : row.curr <= row.target;
        const diff = row.curr - row.prev;
        const barWidth = Math.abs(currPct - prevPct);

        return (
          <div key={i} className="flex items-center gap-2 h-7">
            <span
              className="w-[110px] shrink-0 font-medium text-text-dark-secondary truncate"
              title={row.label}
            >
              {row.label}
            </span>
            <div className="flex-1 relative h-full flex items-center">
              {/* Axis line */}
              <div className="absolute left-0 right-0 h-px bg-nn-border" />
              {/* Target line */}
              <div
                className="absolute top-[3px] bottom-[3px] w-[1.5px] bg-red-critical opacity-55"
                style={{ left: `${targetPct}%` }}
              />
              {/* Previous dot (bottom layer) */}
              <div
                className="absolute w-2.5 h-2.5 rounded-full bg-slate-nn border-2 border-text-muted"
                style={{ left: `${prevPct}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
              />
              {/* Current dot */}
              <div
                className={cn(
                  'absolute w-2.5 h-2.5 rounded-full border-2',
                  atTarget
                    ? 'bg-green-nn border-green-nn'
                    : 'bg-red-critical border-red-critical'
                )}
                style={{ left: `${currPct}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
              />
              {/* Connecting bar (top layer — visible over dots) */}
              <div
                className={cn(
                  'absolute h-[2px] z-10',
                  improving ? 'bg-green-nn' : 'bg-red-critical'
                )}
                style={{ left: `${Math.min(prevPct, currPct)}%`, width: `${Math.max(barWidth, 0.5)}%` }}
              />
            </div>
            <span className="w-[42px] text-right font-bold text-text-dark-secondary shrink-0">
              {row.curr}{unit}
            </span>
            <span
              className={cn(
                'w-[42px] text-right text-body-sm font-bold shrink-0',
                improving ? 'text-green-nn' : 'text-red-critical'
              )}
            >
              {diff > 0 ? '+' : ''}{Math.round(diff * 10) / 10}{unit}
            </span>
          </div>
        );
      })}
    </div>
  );
}
